import { useSelector } from 'react-redux';
import type { MouseEvent, VoidFunctionComponent } from 'react';

import { BINARY_DECOMPOSITION } from '../../../../../../../shared/featureFlags/flagNames';
import { type Fetcher, fetcherToPlainText } from '../../../../../../../shared/fetchers';
import { renderRevision } from '../../../../../../../shared/helpers/renderLocator';
import { useIsFeatureEnabled } from '../../../../../../helpers/features';
import { useGetDependencyConfidenceByDependencyScope } from '../../../../../../hooks/api/binary';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import { Badge, BadgeColors, BUTTON_TYPE, icons } from '../../../../../generic';
import Button from '../../../../../generic/Button';
import FetcherIcon from '../../../../../generic/FetcherIcon';
import Flex from '../../../../../generic/Flex';
import Tooltip from '../../../../../generic/Tooltip';
import TooltipIcon from '../../../../../generic/TooltipIcon';
import BinaryComponentBadge from '../../../../../shared/revisions/BinaryComponentBadge';
import PartialResultGate from '../../../../generic/PartialResult/PartialResultGate';
import type Locator from '../../../../../../../shared/Locator';
import type { DependencyV2Modals } from '../../../hooks/useDependencyModals';

import styles from '../DependencyRow.module.less';

export type DependencyTitleProps = {
  title: string;
  revision: Locator;
  version?: string;
  depth: number;
  layerDepth?: number;
  isManual: boolean;
  isBlocked: boolean;
  hasMalwareIssue: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  modals: DependencyV2Modals;
};

const BlockedBadge: VoidFunctionComponent = () => (
  <Badge color={BadgeColors.Red}>Blocked</Badge>
);

const ManualBadge: VoidFunctionComponent = () => (
  <Badge color={BadgeColors.LightBlue}>Manual Dependency</Badge>
);

const MalwareBadge: VoidFunctionComponent = () => (
  <Badge color={BadgeColors.DarkRed}>Malware</Badge>
);

type DepthBadgeProps = {
  isDirect: boolean;
};

const DepthBadge: VoidFunctionComponent<DepthBadgeProps> = ({ isDirect }) => (
  <Badge color={BadgeColors.LighterGray}>{isDirect ? 'Direct' : 'Transitive'}</Badge>
);
type LayerDepthIconProps = {
  isBase: boolean;
};

const LayerDepthIcon: VoidFunctionComponent<LayerDepthIconProps> = ({ isBase }) => (
  <TooltipIcon
    icon={isBase ? icons.sendBackward : icons.layerGroup}
    tooltip={`Layer: ${isBase ? 'Base' : 'Other'} Layer`}
    id={`depth-icon-${isBase ? 'base' : 'other'}`}
  />
);

const DependencyTitle: VoidFunctionComponent<DependencyTitleProps> = ({
  title,
  revision,
  depth,
  layerDepth,
  isBlocked,
  isManual,
  hasMalwareIssue,
  version,
  onClick,
  modals,
}) => {
  const versionTag = version || renderRevision(revision.toParts());
  const fetcher = revision.fetcher.full || '';
  const fetcherName = fetcherToPlainText(fetcher as Fetcher) ?? 'Unknown';

  const scope = useSelector(DependenciesV2Store.selectors.getScope);
  const isBinary = fetcher === 'csbinary';
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const { confidence, isLoading, isEnabled } = useGetDependencyConfidenceByDependencyScope(
    scope,
    revision.toFullString(),
  );

  const openBinaryMatchDetailsModal = () =>
    modals.viewBinaryMatchDetailsModal.onOpen({
      context: { revision: revision.toFullString() },
    });

  return (
    <Flex gap="8px" align="center">
      <Button type={BUTTON_TYPE.TEXT} onClick={onClick}>
        <div className={styles.DependencyInfoTitle}>
          {/* Package Info */}
          <div className={styles.fetcher}>
            <Tooltip id={`${revision}-fetcher`} tooltip={fetcherName}>
              {/* Note that this span is required to enable the tooltip on an SVG. */}
              <span>
                <FetcherIcon fetcher={fetcher} />
              </span>
            </Tooltip>
          </div>
          <span className={styles.title}>{title}</span>
          <div className={styles.revision}>
            (<span>{versionTag}</span>)
          </div>
          <DepthBadge isDirect={depth <= 1} />
          {layerDepth !== undefined && <LayerDepthIcon isBase={layerDepth === 0} />}
          {isBlocked && <BlockedBadge />}
          {isManual && <ManualBadge />}
          {hasMalwareIssue && <MalwareBadge />}
        </div>
      </Button>
      {isBinary && (
        <BinaryComponentBadge confidence={confidence} isLoading={isLoading} isEnabled={isEnabled} />
      )}
      {isBinary && isBinaryDecompositionEnabled && (
        <PartialResultGate>
          <Button type={BUTTON_TYPE.ANCHOR} onClick={openBinaryMatchDetailsModal}>
            Match Details
          </Button>
        </PartialResultGate>
      )}
    </Flex>
  );
};

export default DependencyTitle;
